// ============================================================================
// Finance view — one fetch, one basis (CW-Q).
//
// Page 1 and Page 2 MUST tie to the cent. The only way to guarantee that is for
// both to aggregate the SAME row set, so this hook fetches v_finance_all once
// and both pages roll it up client-side. A second query with its own predicate
// is how two pages drift apart between one open and the next.
//
// Thresholds come from v_finance_thresholds (app_settings), never a literal.
// Data-quality faults come from v_finance_data_quality — §12 as guards.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../shared/supabase';
import type { MarginThresholds } from './finance-lib';

/** One building in the reported book. Mirrors public.v_finance_all. */
export interface FinanceRow {
  facility_id: string;
  facility_name: string | null;
  account_id: string | null;
  account_name: string | null;
  account_manager_eid: string | null;
  account_manager_name: string | null;
  market_region: string | null;
  territory: string | null;
  service_line: string;
  revenue: number;
  sub_cost: number;
  has_recorded_cost: boolean;
  next_kicker_date: string | null;
  is_hospital: boolean;
  basis_label: string | null;
  /**
   * Why a revenue figure is present or absent. 'tracked' is an ordinary billed
   * building. A hospital may be 'billed_under_parent' (the OPRMC ERs bill under
   * OPRMC) or 'no_separate_revenue' (an Active hospital relationship with nothing
   * separately billed this period). Both carry revenue NULL, which arrives here as
   * 0 -- so a screen must branch on THIS, not on `revenue === 0`, or it prints a
   * $0 that reads as a collapse rather than as an absence.
   */
  revenue_basis: 'tracked' | 'billed_under_parent' | 'no_separate_revenue';
}

export interface DataQualityRow {
  check_key: string;
  check_label: string;
  facility_id: string;
  facility_name: string | null;
  detail: string;
}

export interface FutureStartRow {
  facility_id: string;
  facility_name: string | null;
  account_name: string | null;
  market_region: string | null;
  facility_type: string | null;
  revenue: number | null;
  /** NULL means no cost recorded. It is NEVER shown as $0. */
  sub_cost: number | null;
  contract_start: string | null;
  planned_start_date: string | null;
  exclusion_reason: string;
}

export interface ExclusionRow {
  facility_id: string;
  exclusion_kind: string;
  exclusion_reason: string;
  show_in_separate_list: boolean;
}

/**
 * A known-wrong value behind the Page 2 corrections toggle. The toggle is
 * rendered ONLY while this list is non-empty, which is what makes "temporary by
 * design" true rather than aspirational.
 */
export interface CorrectionRow {
  facility_id: string;
  field: string;
  as_recorded: number;
  corrected: number;
  correction_reason: string;
  annual_impact: number | null;
  owner: string;
  raised_on: string;
}

export interface RevenueTrendRow {
  period_month: string;
  is_actual: boolean;
  revenue: number;
}

export interface ForecastVersion {
  label: string;
  asOf: string;
}

export interface HospitalWeekRow {
  week_ending: string;
  billed_that_week: number;
  hospitals: number;
}

export interface FinanceData {
  rows: FinanceRow[];
  thresholds: MarginThresholds | null;
  dataQuality: DataQualityRow[];
  futureStarts: FutureStartRow[];
  exclusions: ExclusionRow[];
  corrections: CorrectionRow[];
  /** null = the panel could not be filled. Render nothing, name the blocker. */
  revenueTrend: RevenueTrendRow[] | null;
  /** Which model Band 1 is drawing. On screen, so a stale upload is visible. */
  forecastVersion: ForecastVersion | null;
  revenueTrendBlocker: string | null;
  hospitalWeeks: HospitalWeekRow[] | null;
  hospitalWeeksBlocker: string | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refresh: () => void;
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};

const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The forecast series behind "Road to the first $1m month". Manual refresh by
 * design — Wes sends the model monthly and it is uploaded — so an empty result
 * is a real state, not an error, and the panel is REMOVED rather than filled
 * with plausible numbers.
 *
 * ⚠️ WE READ THE LATEST VERSION, NEVER A PINNED LABEL. This was hardcoded to
 * `'TCS Forecast Model 2026-2028 v4.30.26'` from 2026-08-14 to 2026-08-20, so
 * the band drew the APRIL model while Wes had sent May, June and July. Five
 * 2026 actuals were stale, July was flagged as a closed month when Wes has it
 * as a forecast, and August read $1,019,544 against Wes's $1,116,128. Nothing
 * looked broken; it was just a different curve. A pinned version turns "upload
 * the workbook" into "upload the workbook AND ship a code change", and the
 * second half will not happen.
 */
const FORECAST_SCOPE = 'annual_2026_2028';

export function useFinanceData(enabled: boolean): FinanceData {
  const [rows, setRows] = useState<FinanceRow[]>([]);
  const [thresholds, setThresholds] = useState<MarginThresholds | null>(null);
  const [dataQuality, setDataQuality] = useState<DataQualityRow[]>([]);
  const [futureStarts, setFutureStarts] = useState<FutureStartRow[]>([]);
  const [exclusions, setExclusions] = useState<ExclusionRow[]>([]);
  const [corrections, setCorrections] = useState<CorrectionRow[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<RevenueTrendRow[] | null>(null);
  const [forecastVersion, setForecastVersion] = useState<ForecastVersion | null>(null);
  const [revenueTrendBlocker, setRevenueTrendBlocker] = useState<string | null>(null);
  const [hospitalWeeks, setHospitalWeeks] = useState<HospitalWeekRow[] | null>(null);
  const [hospitalWeeksBlocker, setHospitalWeeksBlocker] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      const [book, thr, dq, fut, exc, corr, fcv, fc, hw] = await Promise.all([
        supabase
          .from('v_finance_all')
          // NOTE: one string literal, not a concatenation. supabase-js infers the
          // row type from the LITERAL select string; `'a, b' + 'c'` widens to
          // `string` and every column then types as GenericStringError.
          .select('facility_id, facility_name, account_id, account_name, account_manager_eid, account_manager_name, market_region, territory, service_line, revenue, sub_cost, has_recorded_cost, next_kicker_date, is_hospital, basis_label, revenue_basis'),
        supabase.from('v_finance_thresholds').select('target_pct, amber_floor_pct').maybeSingle(),
        supabase
          .from('v_finance_data_quality')
          .select('check_key, check_label, facility_id, facility_name, detail'),
        supabase
          .from('v_finance_future_starts')
          .select('facility_id, facility_name, account_name, market_region, facility_type, revenue, sub_cost, contract_start, planned_start_date, exclusion_reason'),
        supabase
          .from('v_finance_exclusions')
          .select('facility_id, exclusion_kind, exclusion_reason, show_in_separate_list'),
        supabase
          .from('v_finance_corrections')
          .select('facility_id, field, as_recorded, corrected, correction_reason, annual_impact, owner, raised_on'),
        supabase
          .from('forecasts')
          .select('forecast_id, version_label, as_of_date')
          .eq('scope', FORECAST_SCOPE)
          .order('as_of_date', { ascending: false }),
        // No PostgREST embed here on purpose. `forecasts!inner(...)` resolves
        // through a foreign key, and when that key was missing the whole band
        // died with a message that read as a permissions error. Two queries and
        // a join in JS cannot fail that way.
        supabase
          .from('forecast_lines')
          .select('forecast_id, period_month, is_actual, value')
          .eq('metric', 'revenue')
          .eq('scope_segment', 'total')
          .gte('period_month', '2025-07-01')
          .lte('period_month', '2026-12-31')
          .order('period_month'),
        supabase
          .from('client_billing_lines')
          .select('week_ending, line_amount')
          .eq('source', 'ar_logbook')
          .eq('dept', 'TOTAL (AR Logbook)'),
      ]);

      if (cancelled) return;

      if (book.error) {
        setError(book.error.message);
        setLoading(false);
        return;
      }

      setRows(
        (book.data ?? []).map((r) => ({
          facility_id: String(r.facility_id),
          facility_name: r.facility_name ?? null,
          account_id: r.account_id ?? null,
          account_name: r.account_name ?? null,
          account_manager_eid: r.account_manager_eid ?? null,
          account_manager_name: r.account_manager_name ?? null,
          market_region: r.market_region ?? null,
          territory: r.territory ?? null,
          service_line: String(r.service_line),
          revenue: num(r.revenue),
          sub_cost: num(r.sub_cost),
          has_recorded_cost: r.has_recorded_cost !== false,
          next_kicker_date: r.next_kicker_date ?? null,
          is_hospital: r.is_hospital === true,
          basis_label: r.basis_label ?? null,
          revenue_basis:
            r.revenue_basis === 'billed_under_parent' ||
            r.revenue_basis === 'no_separate_revenue'
              ? r.revenue_basis
              : 'tracked',
        })),
      );

      // A missing threshold row is NOT a reason to fall back to a literal. The
      // page refuses to band anything and says why.
      const t = thr.data;
      setThresholds(
        t && t.target_pct !== null && t.amber_floor_pct !== null
          ? { target_pct: num(t.target_pct), amber_floor_pct: num(t.amber_floor_pct) }
          : null,
      );

      setDataQuality(
        (dq.data ?? []).map((r) => ({
          check_key: String(r.check_key),
          check_label: String(r.check_label),
          facility_id: String(r.facility_id),
          facility_name: r.facility_name ?? null,
          detail: String(r.detail ?? ''),
        })),
      );

      setFutureStarts(
        (fut.data ?? []).map((r) => ({
          facility_id: String(r.facility_id),
          facility_name: r.facility_name ?? null,
          account_name: r.account_name ?? null,
          market_region: r.market_region ?? null,
          facility_type: r.facility_type ?? null,
          revenue: numOrNull(r.revenue),
          sub_cost: numOrNull(r.sub_cost),
          contract_start: r.contract_start ?? null,
          planned_start_date: r.planned_start_date ?? null,
          exclusion_reason: String(r.exclusion_reason ?? ''),
        })),
      );

      setExclusions(
        (exc.data ?? []).map((r) => ({
          facility_id: String(r.facility_id),
          exclusion_kind: String(r.exclusion_kind),
          exclusion_reason: String(r.exclusion_reason),
          show_in_separate_list: r.show_in_separate_list === true,
        })),
      );

      setCorrections(
        (corr.data ?? []).map((r) => ({
          facility_id: String(r.facility_id),
          field: String(r.field),
          as_recorded: num(r.as_recorded),
          corrected: num(r.corrected),
          correction_reason: String(r.correction_reason ?? ''),
          annual_impact: numOrNull(r.annual_impact),
          owner: String(r.owner ?? ''),
          raised_on: String(r.raised_on ?? ''),
        })),
      );

      // §9 — value is in $THOUSANDS in forecast_lines.
      const latest = (fcv.data ?? [])[0] ?? null;
      const lines = (fc.data ?? []).filter(
        (r) => latest !== null && r.forecast_id === latest.forecast_id,
      );
      setForecastVersion(
        latest ? { label: String(latest.version_label), asOf: String(latest.as_of_date) } : null,
      );
      if (fcv.error || fc.error) {
        setRevenueTrend(null);
        setRevenueTrendBlocker(
          `The forecast series could not be read (${(fcv.error ?? fc.error)?.message}). ` +
            'forecasts / forecast_lines are readable by finance, executive and superuser only.',
        );
      } else if (!latest) {
        setRevenueTrend(null);
        setRevenueTrendBlocker(
          `No forecast has been uploaded for scope "${FORECAST_SCOPE}". This series is a ` +
            'MANUAL monthly upload of the forecast model Finance receives — one upload after each close ' +
            'fills it. Until then the panel is removed rather than estimated.',
        );
      } else if (lines.length === 0) {
        setRevenueTrend(null);
        setRevenueTrendBlocker(
          `"${latest.version_label}" (as of ${latest.as_of_date}) is the newest model on ` +
            'file and it carries no monthly revenue lines in range. The version row loaded ' +
            'but its lines did not, which is an upload that stopped halfway.',
        );
      } else {
        setRevenueTrendBlocker(null);
        setRevenueTrend(
          lines.map((r) => ({
            period_month: String(r.period_month),
            is_actual: r.is_actual === true,
            revenue: num(r.value) * 1000,
          })),
        );
      }

      // §10 — hospital weekly run rate. Filter by `source` ALWAYS: a second
      // source (antonela_hour_report) writes the same grain and summing the
      // whole table double-counts.
      if (hw.error) {
        setHospitalWeeks(null);
        setHospitalWeeksBlocker(
          `Weekly hospital billing is not readable for this user (${hw.error.message}).`,
        );
      } else {
        const byWeek = new Map<string, { total: number; n: number }>();
        for (const r of hw.data ?? []) {
          const wk = String(r.week_ending);
          const cur = byWeek.get(wk) ?? { total: 0, n: 0 };
          cur.total += num(r.line_amount);
          cur.n += 1;
          byWeek.set(wk, cur);
        }
        const weeks = Array.from(byWeek.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([week_ending, v]) => ({
            week_ending,
            billed_that_week: v.total,
            hospitals: v.n,
          }));
        setHospitalWeeks(weeks.length ? weeks : null);
        setHospitalWeeksBlocker(
          weeks.length
            ? null
            : 'No ar_logbook weeks are loaded. The AR Logbook load stopped after ' +
                'week-ending 2026-06-28 and the QBO connector (Job 1) is not built.',
        );
      }

      setLastUpdated(new Date());
      setLoading(false);
    })().catch((e: unknown) => {
      if (cancelled) return;
      setError(e instanceof Error ? e.message : String(e));
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, tick]);

  return useMemo(
    () => ({
      rows,
      thresholds,
      dataQuality,
      futureStarts,
      exclusions,
      corrections,
      revenueTrend,
      forecastVersion,
      revenueTrendBlocker,
      hospitalWeeks,
      hospitalWeeksBlocker,
      loading,
      error,
      lastUpdated,
      refresh,
    }),
    [
      rows,
      thresholds,
      dataQuality,
      futureStarts,
      exclusions,
      corrections,
      revenueTrend,
      forecastVersion,
      revenueTrendBlocker,
      hospitalWeeks,
      hospitalWeeksBlocker,
      loading,
      error,
      lastUpdated,
      refresh,
    ],
  );
}

/**
 * The reported month. Hospitals arrive as July 2026 AR Logbook actuals, so the
 * whole page is stamped July 2026 until Job 1 lands and the hospital feed moves
 * with the calendar. Named here, once, and rendered in the header, the first
 * band title and the footer — a page that says "the book" without a month is
 * unciteable in a board pack.
 */
export const REPORTED_MONTH = '2026-07-01';
